import type { BillingCycle } from '@features/onboarding/hooks/use-plans';
import { resolvePlanTier } from '@/shared/ui/components/basic/icons/PlanIcons';

/**
 * Pure model behind PlanFlowModal: what kind of change the buyer is looking
 * at, what it costs today, and which words go with it.
 *
 * Kept free of React and of i18n lookups so the decisions can be tested on
 * their own — the component asks these functions and renders the answer.
 */

/** Which flow the modal is serving. One modal, three jobs. */
export type PlanFlowMode = 'plan' | 'card' | 'cancel';

/**
 * Where inside the flow the buyer is.
 *
 * 'card' is a step and not a mode of its own: the plan flow reaches it when
 * the charge was refused and the buyer wants to pay with another card, and
 * comes back to the review afterwards.
 */
export type PlanFlowStep = 'review' | 'card' | 'processing' | 'done';

/**
 * What a plan change amounts to.
 *
 * `none` is a real answer, not an error: the selector starts on the current
 * plan, and the modal has to say so instead of offering an upgrade to where
 * the agency already is.
 */
export type PlanFlowKind =
  | 'upgrade'
  | 'downgrade'
  | 'to-yearly'
  | 'to-monthly'
  | 'none';

/** One end of the comparison: a plan and the cycle it is billed on. */
export interface PlanSide {
  /** Tier key ('emerald' | 'ruby' | 'diamond') or the plan slug. */
  planKey: string;
  cycle: BillingCycle;
}

const TIER_RANK: Record<string, number> = { emerald: 0, ruby: 1, diamond: 2 };

/** Rank a plan among the tiers, so "higher" is a comparison and not a price. */
function rankOf(planKey: string): number {
  return TIER_RANK[resolvePlanTier(planKey)] ?? 0;
}

/**
 * Classify a change from *current* to *target*.
 *
 * The tier decides upgrade from downgrade — not the price — because a yearly
 * plan costs more per charge than a monthly one of the same tier, and calling
 * that an upgrade would charge pro-rata for a move that is only a change of
 * cycle. The cycle decides only when the tier stands still.
 *
 * @param current - What the agency is on today.
 * @param target - What the selector is pointing at.
 * @returns The kind of change, or 'none' when both ends match.
 */
export function kindOf(current: PlanSide, target: PlanSide): PlanFlowKind {
  const currentRank = rankOf(current.planKey);
  const targetRank = rankOf(target.planKey);

  if (targetRank > currentRank) return 'upgrade';
  if (targetRank < currentRank) return 'downgrade';
  if (current.cycle === target.cycle) return 'none';
  return target.cycle === 'yearly' ? 'to-yearly' : 'to-monthly';
}

/** Kinds the gateway charges for the moment they are confirmed. */
const CHARGED_TODAY: ReadonlySet<PlanFlowKind> = new Set<PlanFlowKind>([
  'upgrade',
  'to-yearly',
]);

/**
 * Whether confirming this change takes money today.
 *
 * Downgrades and the move back to monthly are scheduled for the renewal, so
 * nothing is charged and no card is needed to confirm them.
 *
 * @param kind - The classified change.
 * @returns True when the confirmation charges the card on file.
 */
export function chargesToday(kind: PlanFlowKind): boolean {
  return CHARGED_TODAY.has(kind);
}

/**
 * Whether a change is a trial change: the free period, where every real
 * change applies at once and nothing is charged until the period ends.
 *
 * @param trialing - The subscription is in its free period.
 * @param kind - The classified change.
 * @returns True when the trial wording and billing rows apply.
 */
export function isTrialChange(trialing: boolean | undefined, kind: PlanFlowKind): boolean {
  return Boolean(trialing) && kind !== 'none';
}

/**
 * Whether the flow must ask the server about this change.
 *
 * Active: only a change that charges has a pro-rata worth asking for. In the
 * free period every change is previewed, because the preview carries the
 * conversion figure and is where a pending first payment is refused.
 *
 * @param kind - The classified change.
 * @param trialing - The subscription is in its free period.
 * @returns True when a preview should be requested.
 */
export function needsPreview(kind: PlanFlowKind, trialing: boolean | undefined): boolean {
  return isTrialChange(trialing, kind) || chargesToday(kind);
}

/**
 * What the buyer pays on confirming, in cents.
 *
 * The amount is the server's: the pro-rata preview, never a subtraction done
 * here. A change that is scheduled costs nothing today, whatever the preview
 * happens to hold.
 *
 * @param kind - The classified change.
 * @param proRataCents - `pro_rata_amount_cents` from the preview, if it arrived.
 * @returns Cents to charge today, or null when the amount is not known yet.
 */
export function todayCharge(
  kind: PlanFlowKind,
  proRataCents: number | null | undefined,
): number | null {
  if (!chargesToday(kind)) return 0;
  return typeof proRataCents === 'number' ? proRataCents : null;
}

/**
 * Whether the change is applied now or waits for the renewal.
 *
 * @param kind - The classified change.
 * @returns True when the new plan only starts at the next renewal.
 */
export function isScheduled(kind: PlanFlowKind): boolean {
  return kind === 'downgrade' || kind === 'to-monthly';
}

/** i18n key suffixes, so the component looks up one namespace consistently. */
export interface PlanFlowCopyKeys {
  /** Small mono label above the title. */
  eyebrow: string;
  /** Modal title. */
  title: string;
  /** Primary button label. */
  confirm: string;
  /** Title of the success step. */
  doneTitle: string;
  /** Body of the success step. */
  doneBody: string;
  /** Toast shown alongside the success step. */
  toast: string;
}

const PLAN_COPY: Record<PlanFlowKind, PlanFlowCopyKeys> = {
  upgrade: {
    eyebrow: 'planFlow.eyebrow.plan',
    title: 'planFlow.title.upgrade',
    confirm: 'planFlow.confirm.upgrade',
    doneTitle: 'planFlow.done.upgrade.title',
    doneBody: 'planFlow.done.upgrade.body',
    toast: 'planFlow.toast.upgrade',
  },
  downgrade: {
    eyebrow: 'planFlow.eyebrow.plan',
    title: 'planFlow.title.downgrade',
    confirm: 'planFlow.confirm.schedule',
    doneTitle: 'planFlow.done.downgrade.title',
    doneBody: 'planFlow.done.scheduled.body',
    toast: 'planFlow.toast.scheduled',
  },
  'to-yearly': {
    eyebrow: 'planFlow.eyebrow.plan',
    title: 'planFlow.title.toYearly',
    confirm: 'planFlow.confirm.toYearly',
    doneTitle: 'planFlow.done.toYearly.title',
    doneBody: 'planFlow.done.upgrade.body',
    toast: 'planFlow.toast.toYearly',
  },
  'to-monthly': {
    eyebrow: 'planFlow.eyebrow.plan',
    title: 'planFlow.title.toMonthly',
    confirm: 'planFlow.confirm.scheduleChange',
    doneTitle: 'planFlow.done.toMonthly.title',
    doneBody: 'planFlow.done.scheduled.body',
    toast: 'planFlow.toast.scheduled',
  },
  none: {
    eyebrow: 'planFlow.eyebrow.plan',
    title: 'planFlow.title.none',
    confirm: 'planFlow.confirm.none',
    doneTitle: 'planFlow.title.none',
    doneBody: 'planFlow.done.scheduled.body',
    toast: 'planFlow.toast.scheduled',
  },
};

const MODE_COPY: Record<Exclude<PlanFlowMode, 'plan'>, PlanFlowCopyKeys> = {
  card: {
    eyebrow: 'planFlow.eyebrow.card',
    title: 'planFlow.title.card',
    confirm: 'planFlow.confirm.card',
    doneTitle: 'planFlow.done.card.title',
    doneBody: 'planFlow.done.card.body',
    toast: 'planFlow.toast.card',
  },
  cancel: {
    eyebrow: 'planFlow.eyebrow.cancel',
    title: 'planFlow.title.cancel',
    confirm: 'planFlow.confirm.cancel',
    doneTitle: 'planFlow.done.cancel.title',
    doneBody: 'planFlow.done.cancel.body',
    toast: 'planFlow.toast.cancel',
  },
};

/** What differs in the free period: the change applies now, never "scheduled". */
const TRIAL_OVERRIDES: Partial<Record<PlanFlowKind, Partial<PlanFlowCopyKeys>>> = {
  upgrade: { confirm: 'planFlow.confirm.trialSwitch', toast: 'planFlow.toast.trialChanged' },
  downgrade: {
    title: 'planFlow.title.trialSwitch',
    confirm: 'planFlow.confirm.trialSwitch',
    toast: 'planFlow.toast.trialChanged',
  },
  'to-yearly': { confirm: 'planFlow.confirm.trialToYearly', toast: 'planFlow.toast.trialChanged' },
  'to-monthly': { confirm: 'planFlow.confirm.trialToMonthly', toast: 'planFlow.toast.trialChanged' },
};

/**
 * The copy keys for the current mode and kind.
 *
 * @param mode - Which job the modal is doing.
 * @param kind - The classified change, when the mode is 'plan'.
 * @param trialing - The subscription is in its free period.
 * @returns The i18n keys for eyebrow, title, button and success step.
 */
export function copyKeysFor(
  mode: PlanFlowMode,
  kind: PlanFlowKind,
  trialing?: boolean,
): PlanFlowCopyKeys {
  if (mode !== 'plan') return MODE_COPY[mode];
  if (!isTrialChange(trialing, kind)) return PLAN_COPY[kind];
  return { ...PLAN_COPY[kind], ...TRIAL_OVERRIDES[kind] };
}

/**
 * Whether the primary button may be pressed.
 *
 * Four things block it, and each has its own reason to exist: a change that
 * is not a change, a price the server has not confirmed, a cancellation whose
 * consequences were not acknowledged, and a request already in flight.
 *
 * @param args.mode - Which job the modal is doing.
 * @param args.kind - The classified change.
 * @param args.previewFailed - The pro-rata call errored.
 * @param args.previewPending - The pro-rata call has not answered yet.
 * @param args.cancelChecks - The three cancellation acknowledgements.
 * @param args.busy - A confirmation is already running.
 * @returns True when the button should be disabled.
 */
export function isConfirmDisabled(args: ConfirmArgs): boolean {
  if (args.busy) return true;
  return modeGate(args) ?? planGate(args);
}

/** What the primary button is told about the state of the flow. */
export interface ConfirmArgs {
  mode: PlanFlowMode;
  kind: PlanFlowKind;
  previewFailed?: boolean;
  previewPending?: boolean;
  cancelChecks?: boolean[];
  /** The subscription is in its free period: every change applies now. */
  trialing?: boolean;
  /** The server refused the change and said why; there is nothing to retry. */
  previewRefused?: boolean;
  /** The framed card form has arrived and can be submitted. */
  cardReady?: boolean;
  /** The framed card form is submitting. */
  cardBusy?: boolean;
  busy?: boolean;
}

/**
 * The gate belonging to a mode that is not a plan change.
 *
 * @param args - The same arguments the button is judged on.
 * @returns The verdict, or null when the mode has no gate of its own.
 */
function modeGate(args: ConfirmArgs): boolean | null {
  const { mode, cancelChecks, cardReady, cardBusy } = args;
  if (mode === 'cancel') return !(cancelChecks ?? []).every(Boolean);
  // Submitting a form that is not on screen yet posts the ask into nothing,
  // and the button would simply look broken.
  if (mode === 'card') return !cardReady || Boolean(cardBusy);
  return null;
}

/**
 * The gate of a plan change: a real change, at a price the server confirmed.
 *
 * @param args - The same arguments the button is judged on.
 * @returns True when the plan change may not be confirmed yet.
 */
function planGate(args: ConfirmArgs): boolean {
  const { kind, previewFailed, previewPending, previewRefused } = args;
  if (kind === 'none' || previewRefused) return true;
  if (!needsPreview(kind, args.trialing)) return false;
  return Boolean(previewFailed || previewPending);
}

/**
 * The tier directly below the one the agency is on, among the options given.
 *
 * Used by the cancellation step to offer going down instead of going out.
 * Ranked by tier, never by price: the yearly variant of the same tier costs
 * more per charge and is not a step down.
 *
 * @param options - The plans the flow can switch to.
 * @param currentKey - The plan in force.
 * @returns The option one rung below, or null when there is none.
 */
export function tierBelow<T extends { key: string }>(
  options: T[],
  currentKey: string,
): T | null {
  const currentRank = rankOf(currentKey);
  const lower = options.filter((option) => rankOf(option.key) < currentRank);
  if (lower.length === 0) return null;
  return lower.reduce((best, option) => (
    rankOf(option.key) > rankOf(best.key) ? option : best
  ));
}

/** One limit that differs between the current plan and the target. */
export interface LimitDelta {
  /** Limit key as the catalogue names it. */
  key: string;
  from: number | null;
  to: number | null;
  /** True when the target allows more than the current plan. */
  grows: boolean;
}

/**
 * Limits that actually change, in catalogue order.
 *
 * Only differences are listed: a row saying 10 GB → 10 GB is noise in a
 * screen whose whole job is to answer "what changes if I confirm".
 *
 * @param current - Limits of the plan in force.
 * @param target - Limits of the plan being considered.
 * @returns One entry per limit whose value differs.
 */
export function diffLimits(
  current: Record<string, number | null> | undefined,
  target: Record<string, number | null> | undefined,
): LimitDelta[] {
  if (!target) return [];
  const from = current ?? {};
  return Object.entries(target)
    .filter(([key, to]) => (from[key] ?? null) !== (to ?? null))
    .map(([key, to]) => {
      const before = from[key] ?? null;
      // An absent ceiling means unlimited, which beats any number.
      const grows = to === null || (before !== null && (to ?? 0) > before);
      return { key, from: before, to: to ?? null, grows };
    });
}

/** Features gained and lost by moving to the target plan. */
export interface FeatureDelta {
  gained: string[];
  lost: string[];
}

/**
 * Which features the move turns on, and which it takes away.
 *
 * The lost ones matter more than the gained: they are the reason a downgrade
 * needs a date attached ("deixa de ter a partir de…") rather than a silent
 * confirmation.
 *
 * @param current - Feature flags of the plan in force.
 * @param target - Feature flags of the plan being considered.
 * @returns Keys gained and keys lost.
 */
export function diffFeatures(
  current: Record<string, boolean> | undefined,
  target: Record<string, boolean> | undefined,
): FeatureDelta {
  const from = current ?? {};
  const to = target ?? {};
  const keys = new Set([...Object.keys(from), ...Object.keys(to)]);
  const gained: string[] = [];
  const lost: string[] = [];
  keys.forEach((key) => {
    if (!from[key] && to[key]) gained.push(key);
    if (from[key] && !to[key]) lost.push(key);
  });
  return { gained, lost };
}
